import { useCallback, useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmptyState, Field, PageHeader, StatusBadge } from "@/components/shared/Common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Pick } from "@/pages/gerencia/Sales";
import { todayCL } from "@/pages/operaciones/OpsDialogs";
import { ActionDialog, ActionRow, NewActionDialog, OtFilter, SEVERITY, usePrevLookups } from "./PrevCommon";

const FST = { abierto: ["bloqueado", "Abierto"], en_accion: ["pendiente", "En acción"], cerrado: ["activo", "Cerrado"] };
const SEVS = Object.entries(SEVERITY).map(([id, [, name]]) => ({ id, name }));

const NewFinding = ({ open, onClose, lk, onSaved }) => {
  const [f, setF] = useState(null);
  useEffect(() => { if (open) setF({ ot_id: "", description: "", severity: "media", responsible_id: "", due_date: "" }); }, [open]);
  if (!f) return null;
  const save = async () => { try { await api.post("/prevencion/findings", { ...f, responsible_id: f.responsible_id || null, due_date: f.due_date || null }); toast.success("Hallazgo registrado"); onSaved(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="finding-dialog">
        <DialogHeader><DialogTitle>Nuevo hallazgo</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="OT *"><Pick testid="finding-ot-select" value={f.ot_id} onChange={(v) => setF({ ...f, ot_id: v })} options={lk.ots} placeholder="Seleccione" /></Field>
          <Field label="Gravedad *"><Pick testid="finding-sev-select" value={f.severity} onChange={(v) => setF({ ...f, severity: v })} options={SEVS} /></Field>
          <div className="sm:col-span-2"><Field label="Descripción *"><Textarea data-testid="finding-desc-input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field></div>
          <Field label="Responsable de la acción"><Pick testid="finding-resp-select" value={f.responsible_id} onChange={(v) => setF({ ...f, responsible_id: v })} options={lk.users} placeholder="Opcional" /></Field>
          <Field label="Plazo"><Input data-testid="finding-due-input" type="date" min={todayCL()} value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} /></Field>
        </div>
        <DialogFooter><Button data-testid="finding-save-btn" className="rounded-full" onClick={save}>Registrar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const FindingCard = ({ h, canAdd, onAdd, onOpen }) => (
  <div className="syc-card overflow-hidden" data-testid={`finding-${h.id}`}>
    <div className="flex flex-wrap items-start gap-2 p-4">
      <div className="flex-1 min-w-0"><div className="text-xs font-mono text-[#5b6656]">{h.code} · {h.ot_code} · {h.source === "inspeccion" ? `desde ${h.source_code}` : "manual"} · {fmtDate(h.created_at?.slice(0, 10))}</div><div className="font-semibold">{h.description}</div></div>
      <StatusBadge status={SEVERITY[h.severity][0]} label={SEVERITY[h.severity][1]} /><StatusBadge status={FST[h.status][0]} label={FST[h.status][1]} />
      {canAdd && h.status !== "cerrado" && <Button data-testid={`finding-add-action-${h.id}`} size="sm" variant="outline" className="rounded-full" onClick={() => onAdd(h)}><Plus size={14} className="mr-1" />Acción</Button>}
    </div>
    {h.actions.map((a) => <ActionRow key={a.id} a={a} onOpen={onOpen} />)}
  </div>
);

export default function Findings() {
  const { can } = useAuth();
  const lk = usePrevLookups();
  const [ot, setOt] = useState("");
  const [list, setList] = useState(null);
  const [mine, setMine] = useState([]);
  const [neu, setNeu] = useState(false);
  const [target, setTarget] = useState(null);
  const [action, setAction] = useState(null);
  const load = useCallback(() => {
    api.get("/prevencion/findings", { params: ot ? { ot_id: ot } : {} }).then((r) => setList(r.data));
    api.get("/prevencion/actions", { params: { mine: true } }).then((r) => setMine(r.data.filter((a) => a.status !== "verificada")));
  }, [ot]);
  useEffect(() => { load(); }, [load]);
  const done = () => { setNeu(false); setTarget(null); setAction(null); load(); };
  return (
    <div data-testid="prev-findings-page">
      <PageHeader eyebrow="Prevención" title="Hallazgos y acciones correctivas" subtitle="El responsable informa la ejecución con evidencia y Prevención la verifica para cerrar el hallazgo."
        actions={can("prevencion.hallazgos.crear") && <Button data-testid="finding-new-btn" className="rounded-full" onClick={() => setNeu(true)}><Plus size={15} className="mr-1" />Hallazgo</Button>} />
      <Tabs defaultValue="all">
        <TabsList className="mb-4"><TabsTrigger value="all" data-testid="findings-tab-all">Hallazgos</TabsTrigger><TabsTrigger value="mine" data-testid="findings-tab-mine">Mis acciones ({mine.length})</TabsTrigger></TabsList>
        <TabsContent value="all">
          <div className="mb-4"><OtFilter ots={lk.ots} value={ot} onChange={setOt} /></div>
          <div className="space-y-3">{!list ? "Cargando…" : list.length === 0 ? <div className="syc-card"><EmptyState title="Sin hallazgos" /></div> : list.map((h) => (
            <FindingCard key={h.id} h={h} canAdd={can("prevencion.acciones.gestionar")} onAdd={(x) => setTarget({ finding_id: x.id })} onOpen={setAction} />))}</div>
        </TabsContent>
        <TabsContent value="mine"><div className="syc-card overflow-hidden" data-testid="my-actions">{mine.length === 0 ? <EmptyState title="Sin acciones pendientes" /> : mine.map((a) => <ActionRow key={a.id} a={a} onOpen={setAction} />)}</div></TabsContent>
      </Tabs>
      <NewFinding open={neu} onClose={() => setNeu(false)} lk={lk} onSaved={done} />
      <NewActionDialog target={target} users={lk.users} onClose={() => setTarget(null)} onSaved={done} />
      <ActionDialog action={action} onClose={() => setAction(null)} onSaved={done} />
    </div>
  );
}
